import { BookOpen, Check, Copy } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { AGENCY_TZ } from "../api";
import Markdown from "./Markdown";
import { noDash } from "../insights";

export type ChatMessageData = {
  id: number | string;
  role: string;
  content_text: string;
  prompt_name?: string | null;
  created_at?: string | null;
  /** The action this reply proposed, if any, and its state in Approvals. */
  proposal_id?: number | null;
  proposal_status?: string | null;
};

/** "Oct 2, 3:15 PM", on the agency's clock. */
function timeLabel(value?: string | null) {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: AGENCY_TZ }).format(date);
}

/**
 * A saved reply as the team reads it. Replies written before the analyst was
 * told "no emoji, no em dashes" carry both (status emoji in headings, " — "
 * mid-sentence); new replies are cleaned on the server the same way. A dash
 * standing alone, like an empty table cell, stays.
 */
function plainReply(text: string) {
  return text
    .replace(/[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}]\uFE0F?/gu, "")
    .replace(/[\uFE0F\u200D]/g, "")
    .replace(/([^\s|])[ \t]*—[ \t]*(?=[^\s|])/g, "$1, ")
    .replace(/^(#+|[-*]) {2,}/gm, "$1 ")
    .replace(/([^\s]) {2,}/g, "$1 ")
    .replace(/[ \t]+$/gm, "");
}

/** The small round mark the analyst's replies carry. */
export function AnalystMark() {
  return <span className="ac-mark" aria-hidden="true">NL</span>;
}

/**
 * One message in the analyst thread. Your messages are right-aligned bubbles
 * shown as typed; the analyst's replies are Markdown on the page surface with a
 * Copy action. `children` renders under a reply (the proposed-action card).
 */
export default function ChatMessage({ message, last, children }: { message: ChatMessageData; last?: boolean; children?: ReactNode }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  // Your own words stay exactly as typed; the analyst's read without emoji or em dashes.
  const text = message.role === "assistant" ? plainReply(message.content_text || "") : message.content_text || "";
  const time = timeLabel(message.created_at);

  if (message.role !== "assistant") {
    return (
      <article className="ac-msg ac-msg-user" aria-label="You">
        {message.prompt_name && (
          <span className="ac-prompt-tag"><BookOpen aria-hidden="true" />{noDash(message.prompt_name)}</span>
        )}
        <div className="ac-bubble" title={time}>{text || "No message content recorded."}</div>
      </article>
    );
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* Clipboard blocked (insecure origin or denied): nothing to confirm. */
    }
  }

  return (
    <article className={last ? "ac-msg ac-msg-assistant is-last" : "ac-msg ac-msg-assistant"} aria-label="Analyst">
      <AnalystMark />
      <div className="ac-reply">
        <Markdown>{text || "No message content recorded."}</Markdown>
        <div className="ac-msg-actions">
          <button type="button" className="ac-copy" onClick={copy} aria-label={copied ? "Copied" : "Copy reply"}>
            {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
            <span>{copied ? "Copied" : "Copy"}</span>
          </button>
          {time && <time dateTime={message.created_at || undefined}>{time}</time>}
          <span className="sr-only" aria-live="polite">{copied ? "Reply copied to the clipboard" : ""}</span>
        </div>
        {children}
      </div>
    </article>
  );
}
